import { USER_API_URLS } from '@/config/apiConfig';
import {
  RegisterUserPayload,
  RegisterUserResponse,
  VerifyOtpResponse,
  GetAllUsersResponse,
  GetAllActivityLogsResponse,
  UserDetail,
} from '@/types/user';

export interface RoleProfile {
  name: string;
}

const getAuthToken = (): string => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('token') || '';
};

export const sendOtp = async (phoneNumber: string) => {
  const response = await fetch(USER_API_URLS.SEND_OTP, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneno: phoneNumber }),
  });
  if (!response.ok) throw new Error('Failed to send OTP');
  const result = await response.json();
  return result?.data ?? result;
};

export const verifyOtp = async (phoneNumber: string, code: string) => {
  const response = await fetch(USER_API_URLS.VERIFY_OTP, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneno: phoneNumber, code }),
  });
  if (!response.ok) throw new Error('Failed to verify OTP');
  const result: VerifyOtpResponse = await response.json();
  return result?.data ?? result;
};

export const registerUser = async (userData: RegisterUserPayload) => {
  const response = await fetch(USER_API_URLS.REGISTER_USER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  if (!response.ok) throw new Error('Failed to register user');
  const result: RegisterUserResponse = await response.json();
  return result?.data ?? result;
};

export const getAllUsers = async (): Promise<UserDetail[]> => {
  const token = getAuthToken();
  const response = await fetch(`${USER_API_URLS.GET_ALL_USERS}?limit=500`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: token },
  });
  if (!response.ok) throw new Error('Failed to get all users');
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : [];
};

/** Single user lookup by email — uses get_users with a filter. */
export const getUserByEmail = async (email: string): Promise<UserDetail | null> => {
  const token = getAuthToken();
  const filters = encodeURIComponent(JSON.stringify([['email', '=', email]]));
  const response = await fetch(
    `${USER_API_URLS.GET_ALL_USERS}?filters=${filters}&limit=1`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: token },
    }
  );
  if (!response.ok) throw new Error('Failed to get user by email');
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) && items.length > 0 ? items[0] : null;
};

export const getUserById = async (userId: string): Promise<UserDetail> => {
  const token = getAuthToken();
  const response = await fetch(
    `${USER_API_URLS.GET_USER_BY_ID}?name=${encodeURIComponent(userId)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: token },
    }
  );
  if (!response.ok) throw new Error('Failed to get user by id');
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const createUser = async (userData: any): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(USER_API_URLS.CREATE_USER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(userData),
  });
  if (!response.ok) throw new Error('Failed to create user');
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const updateUser = async (userId: string, userData: any): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(USER_API_URLS.UPDATE_USER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ name: userId, ...userData }),
  });
  if (!response.ok) throw new Error('Failed to update user');
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const deleteUser = async (userId: string): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(USER_API_URLS.DELETE_USER, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify({ name: userId }),
  });
  if (!response.ok) throw new Error('Failed to delete user');
  const result = await response.json();
  return result?.data ?? result;
};

export const getAllActivityLogs = async (): Promise<any[]> => {
  const token = getAuthToken();
  const response = await fetch(`${USER_API_URLS.GET_ALL_ACTIVITY_LOGS}?limit=500`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: token },
  });
  if (!response.ok) throw new Error('Failed to get all activity logs');
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : [];
};
